import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BackendPage, Page, getPageMapper, getPagesMapper } from 'src/config/api/models/Page';
import { TrajectorySectionOrWorksheetType } from 'src/config/api/models/Trajectory';
import apiRoutesV2, { baseURL } from 'src/config/api/routes';
import { RootState } from 'src/modules/shared/store';

type getPagesParams = {
  trajectoryId: string;
  type: TrajectorySectionOrWorksheetType;
};

type addPageParams = {
  bookId: string;
  trajectoryId: string;
  type: TrajectorySectionOrWorksheetType;
};

export const pagesApi = createApi({
  reducerPath: 'pagesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPages: builder.query<Page[], getPagesParams>({
      query: ({ trajectoryId, type }: getPagesParams) => ({
        url: apiRoutesV2.pagesRelativePath(trajectoryId, type),
        method: 'GET',
      }),
      transformResponse: (response: { data: BackendPage }) => getPagesMapper(response),
    }),
    togglePageIsPrintable: builder.mutation<Page, { bookId: string; pageId: string }>({
      query: ({ bookId, pageId }) => ({
        url: apiRoutesV2.togglePageIsPrintableRelativePath(bookId, pageId),
        method: 'PATCH',
      }),
      transformResponse: (response: { data: BackendPage }) => getPageMapper(response),
    }),
    addPage: builder.mutation<Page, addPageParams>({
      query: ({ bookId, trajectoryId, type }: addPageParams) => ({
        url: apiRoutesV2.bookTrajectoryPagesRelativePath(bookId, trajectoryId),
        method: 'POST',
        body: { type },
      }),
      transformResponse: (response: { data: BackendPage }) => getPageMapper(response),
      async onQueryStarted({ bookId, trajectoryId, type }, { dispatch, queryFulfilled }) {
        try {
          const { data: newPage } = await queryFulfilled;
          dispatch(
            pagesApi.util.updateQueryData('getPages', { trajectoryId, type }, (draftPages) => {
              draftPages.push(newPage);
            })
          );
        } catch {}
      },
    }),
  }),
});

export const { useGetPagesQuery, useTogglePageIsPrintableMutation, useAddPageMutation } = pagesApi;
